import Button from 'devextreme-react/button';
import DataGrid, {
  Column,
  Button as GridButton,
  Item,
  Toolbar,
  type DataGridRef
} from 'devextreme-react/data-grid';
import LoadIndicator from 'devextreme-react/load-indicator';
import TagBox from 'devextreme-react/tag-box';
import TextArea from 'devextreme-react/text-area';
import TextBox from 'devextreme-react/text-box';
import {
  GridFormPopup,
  OPS_SITE_GRID_PROPS,
  OpsGridToolbarItems,
  OpsSiteGridChrome,
  onGridFocusedRowEnter,
  onGridRowDblClick,
  opsGridFormPopupSizeKey,
  showDeleteConfirmDialog,
  showErrorToast,
  showSuccessToast
} from '@jarvis/platform-ui';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import {
  createClientAccountsGroup,
  deleteClientAccountsGroup,
  getGroupAppRoles,
  listClientAccountsGroups,
  listClientPortalTenants,
  listPortalAppRoles,
  setGroupAppRoles,
  updateClientAccountsGroup,
  type EntraGroupRow,
  type PortalAppRoleOption
} from '../../../api/clientAccountsAdmin';

const GRID_STATE_KEY = 'webopsui:hvg-ce-groups-grid:v6';
const GRID_SCHEMA_VERSION = 4;
const GRID_COLUMNS = [
  'id',
  'displayName',
  'description',
  'securityEnabled',
  'assignedSprRoles',
  'createdDateTime'
] as const;

const fieldLabel: CSSProperties = {
  fontWeight: 600,
  marginBottom: 6,
  fontSize: 13,
  display: 'block'
};

type GroupGridRow = EntraGroupRow;

type GroupFormState = {
  displayName: string;
  description: string;
  assignedSprRoles: string[];
};

function emptyForm(): GroupFormState {
  return { displayName: '', description: '', assignedSprRoles: [] };
}

function toGridRow(row: EntraGroupRow): GroupGridRow {
  return row;
}

function sortRows(rows: GroupGridRow[]): GroupGridRow[] {
  return [...rows].sort((a, b) => a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' }));
}

function sameRoleSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const left = [...a].map((v) => v.toLowerCase()).sort();
  const right = [...b].map((v) => v.toLowerCase()).sort();
  return left.every((v, i) => v === right[i]);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isEntraReplicationError(message: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes('not a valid reference update') ||
    m.includes('request_resourcenotfound') ||
    m.includes('does not exist') ||
    m.includes('not yet') ||
    m.includes('timed out waiting for entra')
  );
}

/** Entra often needs a few seconds after create before app-role links work; edit is instant later. */
async function assignRolesAfterCreate(
  tenantId: string,
  groupId: string,
  roleValues: string[]
): Promise<string[]> {
  // Short settle, then retry with backoff — runs in the background so the create dialog can close.
  const retryDelaysMs = [3_000, 4_000, 5_000, 6_000, 8_000, 10_000];
  await delay(2_000);

  let lastError: string | null = null;
  for (let attempt = 0; attempt < retryDelaysMs.length + 1; attempt++) {
    try {
      return await setGroupAppRoles(tenantId, groupId, roleValues, { newGroup: true });
    } catch (e) {
      lastError = e instanceof Error ? e.message : String(e);
      if (!isEntraReplicationError(lastError) || attempt >= retryDelaysMs.length) break;
      await delay(retryDelaysMs[attempt]!);
    }
  }

  throw new Error(lastError ?? 'SPR role assignment failed');
}

type CreateProgressState = {
  title: string;
  detail: string;
  error: string | null;
  done: boolean;
};

export function HvgCeGroupsAdminPage() {
  const gridRef = useRef<DataGridRef>(null);
  const loadGenRef = useRef(0);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Groups deleted locally; filter from server list until Graph replication catches up. */
  const pendingDeleteIdsRef = useRef<Set<string>>(new Set());
  /** Recent create/update rows — prefer these over stale Graph list results. */
  const pendingUpsertsRef = useRef<Map<string, GroupGridRow>>(new Map());
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantError, setTenantError] = useState<string | null>(null);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [rows, setRows] = useState<GroupGridRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoadPending, setInitialLoadPending] = useState(true);
  /** null = not probed yet (do not show config warnings); true/false = known after load. */
  const [portalAppRolesEnabled, setPortalAppRolesEnabled] = useState<boolean | null>(null);
  const [portalAppRolesError, setPortalAppRolesError] = useState<string | null>(null);
  const [portalAppRoles, setPortalAppRoles] = useState<PortalAppRoleOption[]>([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<GroupFormState>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [createProgress, setCreateProgress] = useState<CreateProgressState | null>(null);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [focusedRowKey, setFocusedRowKey] = useState<string | null>(null);

  const focusedRow = focusedRowKey ? rows.find((r) => r.id === focusedRowKey) ?? null : null;
  const sprRoleItems = useMemo(() => portalAppRoles.map((r) => r.value), [portalAppRoles]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const items = await listClientPortalTenants();
        if (cancelled) return;
        if (items.length === 0) {
          setTenantError('No client tenants are configured. Add ClientGraph__Tenants__N__* to WebOps env.');
          setTenantId(null);
        } else {
          setTenantId(items[0].tenantId);
          setTenantError(null);
        }
      } catch (e: unknown) {
        if (!cancelled) setTenantError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (tenantId) setInitialLoadPending(true);
  }, [tenantId]);

  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []);

  const load = useCallback(async () => {
    if (!tenantId) {
      setRows([]);
      setPortalAppRolesEnabled(null);
      setInitialLoadPending(false);
      return;
    }
    const gen = ++loadGenRef.current;
    setLoading(true);
    try {
      const data = await listClientAccountsGroups(tenantId);
      if (gen !== loadGenRef.current) return;

      const serverIds = new Set(data.items.map((item) => item.id));
      for (const id of pendingDeleteIdsRef.current) {
        if (!serverIds.has(id)) pendingDeleteIdsRef.current.delete(id);
      }

      setRows((prev) => {
        const roleById = new Map(prev.map((r) => [r.id, r.assignedSprRoles] as const));
        return data.items
          .filter((item) => !pendingDeleteIdsRef.current.has(item.id))
          .map((item) => {
            const pending = pendingUpsertsRef.current.get(item.id);
            if (pending) {
              const merged = toGridRow({
                ...item,
                displayName: pending.displayName,
                description: pending.description,
                assignedSprRoles:
                  pending.assignedSprRoles.length > 0
                    ? pending.assignedSprRoles
                    : item.assignedSprRoles?.length
                      ? item.assignedSprRoles
                      : roleById.get(item.id) ?? []
              });
              if (
                item.displayName === pending.displayName &&
                (item.description ?? '') === (pending.description ?? '') &&
                sameRoleSet(item.assignedSprRoles ?? [], pending.assignedSprRoles)
              ) {
                pendingUpsertsRef.current.delete(item.id);
              }
              return merged;
            }

            return toGridRow({
              ...item,
              assignedSprRoles: item.assignedSprRoles?.length
                ? item.assignedSprRoles
                : roleById.get(item.id) ?? []
            });
          });
      });
      setPortalAppRolesEnabled(data.portalAppRolesEnabled);
      setPortalAppRolesError(data.portalAppRolesError ?? null);

      if (data.portalAppRolesEnabled) {
        try {
          if (gen !== loadGenRef.current) return;
          setPortalAppRoles(await listPortalAppRoles(tenantId));
        } catch {
          setPortalAppRoles([]);
        }
      } else {
        setPortalAppRoles([]);
      }
    } catch (e) {
      if (gen !== loadGenRef.current) return;
      setRows([]);
      setPortalAppRolesEnabled(null);
      showErrorToast(e instanceof Error ? e.message : 'Failed to load groups');
    } finally {
      if (gen === loadGenRef.current) {
        setLoading(false);
        setInitialLoadPending(false);
      }
    }
  }, [tenantId]);

  const scheduleRefresh = useCallback((delayMs = 750) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null;
      void load();
    }, delayMs);
  }, [load]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormError(null);
    setCreateProgress(null);
    setLoadingRoles(false);
    setEditorOpen(true);
  };

  const closeEditor = () => {
    if (createProgress && !createProgress.done && !createProgress.error) return;
    setEditorOpen(false);
    setCreateProgress(null);
    setFormError(null);
  };

  const openEdit = async (row: GroupGridRow) => {
    setEditingId(row.id);
    setFocusedRowKey(row.id);
    setForm({
      displayName: row.displayName,
      description: row.description ?? '',
      assignedSprRoles: [...(row.assignedSprRoles ?? [])]
    });
    setFormError(null);
    setCreateProgress(null);
    setEditorOpen(true);

    if (!tenantId || portalAppRolesEnabled !== true) return;

    setLoadingRoles(true);
    try {
      const roles = await getGroupAppRoles(tenantId, row.id);
      setForm((f) => ({ ...f, assignedSprRoles: roles }));
      setRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, assignedSprRoles: roles } : r))
      );
    } catch {
      // Keep whatever roles we already had in the grid row.
    } finally {
      setLoadingRoles(false);
    }
  };

  const save = async () => {
    if (!tenantId) return;
    if (!form.displayName.trim()) {
      setFormError('Display name is required.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      if (editingId == null) {
        const desiredRoles =
          portalAppRolesEnabled === true ? [...form.assignedSprRoles] : [];

        setCreateProgress({
          title: 'Creating group…',
          detail: 'Saving the security group in Entra.',
          error: null,
          done: false
        });

        const created = await createClientAccountsGroup(tenantId, {
          displayName: form.displayName.trim(),
          description: form.description.trim() || null
        });

        // Close immediately — SPR assign often waits on Entra replication (15–60s).
        // Show desired roles optimistically; background retries update or clear them.
        const row = toGridRow({
          ...created,
          assignedSprRoles: desiredRoles,
          createdDateTime: created.createdDateTime ?? new Date().toISOString()
        });
        pendingUpsertsRef.current.set(row.id, row);
        setRows((prev) => sortRows([...prev.filter((r) => r.id !== row.id), row]));
        setCreateProgress(null);
        setEditorOpen(false);
        showSuccessToast(
          desiredRoles.length > 0
            ? 'Group created. Assigning SPR roles in the background…'
            : 'Group created.'
        );
        scheduleRefresh(2500);

        if (desiredRoles.length > 0) {
          void (async () => {
            try {
              const assignedRoles = await assignRolesAfterCreate(tenantId, created.id, desiredRoles);
              const updated = toGridRow({ ...created, assignedSprRoles: assignedRoles });
              pendingUpsertsRef.current.set(updated.id, updated);
              setRows((prev) =>
                sortRows(prev.map((r) => (r.id === updated.id ? { ...r, assignedSprRoles: assignedRoles } : r)))
              );
              showSuccessToast('SPR roles assigned.');
              scheduleRefresh(2500);
            } catch (e) {
              const message = e instanceof Error ? e.message : 'SPR role assignment failed';
              const cleared = toGridRow({ ...created, assignedSprRoles: [] });
              pendingUpsertsRef.current.set(cleared.id, cleared);
              setRows((prev) =>
                sortRows(prev.map((r) => (r.id === cleared.id ? { ...r, assignedSprRoles: [] } : r)))
              );
              showErrorToast(
                `Group created, but SPR roles were not assigned yet (${message}). Open the group and save roles again.`
              );
              scheduleRefresh(2500);
            }
          })();
        }
      } else {
        const updated = await updateClientAccountsGroup(tenantId, editingId, {
          displayName: form.displayName.trim(),
          description: form.description.trim() || null,
          assignedSprRoles: undefined
        });

        let roles = portalAppRolesEnabled === true ? [...form.assignedSprRoles] : updated.assignedSprRoles;
        let roleWarning: string | null = null;
        if (portalAppRolesEnabled === true) {
          try {
            roles = await setGroupAppRoles(tenantId, editingId, form.assignedSprRoles);
          } catch (e) {
            roleWarning = e instanceof Error ? e.message : 'SPR role assignment failed';
          }
        }

        const row = toGridRow({
          ...updated,
          displayName: form.displayName.trim(),
          description: form.description.trim() || null,
          assignedSprRoles: roles
        });
        pendingUpsertsRef.current.set(row.id, row);
        setRows((prev) => sortRows(prev.map((r) => (r.id === row.id ? row : r))));
        showSuccessToast('Group updated.');
        if (roleWarning) {
          showErrorToast(`Group updated, but SPR roles were not saved: ${roleWarning}`);
        }
        setEditorOpen(false);
        scheduleRefresh(2500);
      }
    } catch (e) {
      setCreateProgress(null);
      setFormError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const removeRow = async (row: GroupGridRow) => {
    if (!tenantId) return;
    if (!(await showDeleteConfirmDialog(`Delete group "${row.displayName}"? This cannot be undone.`))) return;
    try {
      await deleteClientAccountsGroup(tenantId, row.id);
      pendingDeleteIdsRef.current.add(row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      if (focusedRowKey === row.id) setFocusedRowKey(null);
      showSuccessToast('Group deleted.');
      if (editingId === row.id) setEditorOpen(false);
      scheduleRefresh();
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to delete group');
    }
  };

  const removeFromEditor = async () => {
    if (!tenantId || editingId == null) return;
    const row = rows.find((r) => r.id === editingId);
    if (!row) return;
    setSaving(true);
    try {
      await removeRow(row);
    } finally {
      setSaving(false);
    }
  };

  if (bootstrapping) {
    return (
      <div className="ops-fill-page ops-fill-page--bare" style={{ padding: '1rem', display: 'flex', gap: 8, alignItems: 'center' }}>
        <LoadIndicator visible height={20} width={20} />
        <span>Loading…</span>
      </div>
    );
  }

  if (tenantError || !tenantId) {
    return (
      <div className="ops-fill-page ops-fill-page--bare" style={{ padding: '1rem' }}>
        <div style={{ color: 'var(--dx-color-danger, #dc2626)', fontSize: 14 }}>
          {tenantError ?? 'No client tenant configured.'}
        </div>
      </div>
    );
  }

  if (initialLoadPending) {
    return (
      <div className="ops-fill-page ops-fill-page--bare" style={{ padding: '1rem', display: 'flex', gap: 8, alignItems: 'center' }}>
        <LoadIndicator visible height={20} width={20} />
        <span>Loading groups…</span>
      </div>
    );
  }

  return (
    <div className="ops-fill-page ops-fill-page--bare">
      <div className="ops-fill-page__grid-host">
        <DataGrid
          ref={gridRef}
          dataSource={rows}
          keyExpr="id"
          height="100%"
          width="100%"
          showBorders
          columnAutoWidth
          columnHidingEnabled={false}
          allowColumnReordering
          allowColumnResizing
          columnResizingMode="widget"
          {...OPS_SITE_GRID_PROPS}
          focusedRowEnabled
          focusedRowKey={focusedRowKey ?? undefined}
          onFocusedRowChanged={(e) => {
            const key = e.row?.key;
            setFocusedRowKey(typeof key === 'string' ? key : null);
          }}
          noDataText="No groups found in this tenant."
          onRowDblClick={(e) => onGridRowDblClick(e, (row) => void openEdit(row))}
          onKeyDown={(e) => onGridFocusedRowEnter(e, (row) => void openEdit(row))}
        >
          <OpsSiteGridChrome
            stateStorageKey={`${GRID_STATE_KEY}:${tenantId}`}
            schemaVersion={GRID_SCHEMA_VERSION}
            stateColumns={GRID_COLUMNS}
            searchPlaceholder="Search groups…"
            pageSize={50}
          />
          <Toolbar>
            <OpsGridToolbarItems
              gridRef={gridRef}
              loading={loading}
              onRefresh={() => void load()}
              refreshHint="Refresh groups"
              newItem={{ hint: 'Add group', onClick: openCreate }}
              editItem={{
                hint: 'Edit group',
                active: focusedRow != null,
                onClick: () => {
                  if (focusedRow) void openEdit(focusedRow);
                }
              }}
              deleteItem={{
                hint: 'Delete group',
                active: focusedRow != null,
                onClick: () => {
                  if (focusedRow) void removeRow(focusedRow);
                }
              }}
              export={{ fileName: 'hvg-ce-groups-export', worksheetName: 'CE Groups', hint: 'Export groups' }}
              resetLayout={{ storageKey: `${GRID_STATE_KEY}:${tenantId}` }}
            />
            <Item name="searchPanel" location="after" />
          </Toolbar>
          <Column
            type="buttons"
            name="actions"
            caption="Actions"
            width={90}
            fixed
            fixedPosition="left"
            showInColumnChooser={false}
          >
            <GridButton
              hint="Edit group"
              icon="edit"
              onClick={(e) => {
                const row = e.row?.data as GroupGridRow | undefined;
                if (row) void openEdit(row);
              }}
            />
            <GridButton
              hint="Delete group"
              icon="trash"
              onClick={(e) => {
                const row = e.row?.data as GroupGridRow | undefined;
                if (row) void removeRow(row);
              }}
            />
          </Column>
          <Column
            name="id"
            dataField="id"
            caption="Group ID"
            width={280}
            visible={false}
            allowFiltering
            allowHeaderFiltering
          />
          <Column name="displayName" dataField="displayName" caption="Display name" minWidth={180} sortOrder="asc" />
          <Column name="description" dataField="description" caption="Description" minWidth={220} />
          <Column name="securityEnabled" dataField="securityEnabled" caption="Security" dataType="boolean" width={100} />
          <Column
            name="assignedSprRoles"
            dataField="assignedSprRoles"
            caption="SPR app roles"
            minWidth={200}
            cellRender={({ value }) => (
              <span>{Array.isArray(value) && value.length > 0 ? value.join(', ') : '—'}</span>
            )}
          />
          <Column
            name="createdDateTime"
            dataField="createdDateTime"
            caption="Created"
            dataType="datetime"
            minWidth={160}
            format="yyyy-MM-dd HH:mm"
          />
        </DataGrid>
      </div>

      <GridFormPopup
        visible={editorOpen}
        title={
          createProgress
            ? 'Creating Entra group'
            : editingId == null
              ? 'Add Entra group'
              : 'Edit Entra group'
        }
        onClose={closeEditor}
        sizeStorageKey={opsGridFormPopupSizeKey(GRID_STATE_KEY)}
        error={createProgress ? createProgress.error : formError}
        footer={
          createProgress ? (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
              {createProgress.done || createProgress.error ? (
                <Button text="Close" type="default" onClick={closeEditor} />
              ) : (
                <span style={{ fontSize: 13, color: 'var(--text-muted, #6b7280)' }}>
                  Please wait — do not close this window.
                </span>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
              <div>
                {editingId != null ? (
                  <Button text="Delete" type="danger" disabled={saving} onClick={() => void removeFromEditor()} />
                ) : null}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button text="Cancel" disabled={saving} onClick={closeEditor} />
                <Button
                  text={saving ? 'Saving…' : 'Save'}
                  type="default"
                  disabled={saving || loadingRoles}
                  onClick={() => void save()}
                />
              </div>
            </div>
          )
        }
      >
        {createProgress ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '8px 0', minHeight: 120 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              {!createProgress.done && !createProgress.error ? (
                <LoadIndicator visible height={28} width={28} />
              ) : null}
              <div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{createProgress.title}</div>
                <div style={{ marginTop: 6, fontSize: 13, lineHeight: 1.45, color: 'var(--text-muted, #4b5563)' }}>
                  {createProgress.detail}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <label style={fieldLabel}>Display name</label>
            <TextBox
              value={form.displayName}
              onValueChanged={(e) => setForm((f) => ({ ...f, displayName: e.value ?? '' }))}
              width="100%"
            />
            <label style={{ ...fieldLabel, marginTop: 12 }}>Description</label>
            <TextArea
              value={form.description}
              onValueChanged={(e) => setForm((f) => ({ ...f, description: e.value ?? '' }))}
              height={72}
              width="100%"
            />
            {portalAppRolesEnabled === true ? (
              <>
                <label style={{ ...fieldLabel, marginTop: 12 }}>
                  SPR app roles{loadingRoles ? ' (loading…)' : ''}
                </label>
                <TagBox
                  dataSource={sprRoleItems}
                  value={form.assignedSprRoles}
                  onValueChanged={(e) => setForm((f) => ({ ...f, assignedSprRoles: e.value ?? [] }))}
                  showSelectionControls
                  searchEnabled={false}
                  disabled={loadingRoles}
                  width="100%"
                />
              </>
            ) : portalAppRolesEnabled === false ? (
              <p style={{ marginTop: 12, fontSize: 13, color: 'var(--dx-color-danger, #dc2626)' }}>
                SPR role assignment is unavailable:{' '}
                {portalAppRolesError ?? 'the portal application with SPR-* app roles was not found in this tenant.'}
              </p>
            ) : null}
          </>
        )}
      </GridFormPopup>
    </div>
  );
}
